import React, { useCallback, useMemo, useState } from 'react';
import propTypes from 'prop-types';
import classnames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  TextField,
  InputAdornment,
  Checkbox,
} from '@material-ui/core';
import CloseIcon from 'assets/images/close-icon.svg';
import PencilI from 'assets/images/edit-icon-pencil.svg';
import SearchSelect from '../SearchSelect';
import styles from './styles';

const useStyles = makeStyles(styles);

/**
 * Searchable selector whose list rows can be renamed/deleted inline and which can create
 * a new entry from the pinned footer. Entities are described by `getOptionId` /
 * `getOptionLabel`, so any `{ id, label }`-ish shape works.
 *
 * `placeholderOnly` keeps the field showing its placeholder instead of the selection —
 * for callers that render the selection themselves (chips, a table).
 */
const InlineCreateSelector = ({
  value,
  options,
  onChange,
  onCreate,
  onRename,
  onDelete,
  getOptionId,
  getOptionLabel,
  excludeIds,
  addLabel,
  placeholder,
  disabled,
  multi,
  error,
  name,
  id,
  onBlur,
  placeholderOnly,
}) => {
  const classes = useStyles();

  const [isInput, setIsInput] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const excludeSet = useMemo(
    () => new Set((excludeIds || []).filter(Boolean)),
    [excludeIds],
  );

  const visibleOptions = useMemo(
    () => (options || []).filter((opt) => !excludeSet.has(getOptionId(opt))),
    [options, excludeSet, getOptionId],
  );

  const selectedIds = useMemo(
    () => (multi ? (Array.isArray(value) ? value : []) : [value]).filter(Boolean),
    [multi, value],
  );

  const enterAdd = useCallback(() => {
    setEditingId(null);
    setDraftName('');
    setIsInput(true);
  }, []);

  const enterEdit = useCallback(
    (option) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      setEditingId(getOptionId(option));
      setDraftName(getOptionLabel(option));
      setIsInput(true);
    },
    [getOptionId, getOptionLabel],
  );

  const cancelInput = useCallback(() => {
    setIsInput(false);
    setEditingId(null);
    setDraftName('');
  }, []);

  const submitAdd = useCallback(async () => {
    const trimmed = draftName.trim();
    if (!trimmed || !onCreate) return;
    setBusy(true);
    try {
      const created = await onCreate(trimmed);
      const createdId = created && getOptionId(created);
      if (createdId) {
        if (multi) {
          const next = Array.isArray(value) ? value : [];
          if (!next.includes(createdId)) onChange([...next, createdId]);
        } else {
          onChange(createdId);
        }
      }
      setIsInput(false);
      setDraftName('');
    } catch (_) {
      // host surfaces error; keep input open so user can retry
    } finally {
      setBusy(false);
    }
  }, [draftName, onCreate, onChange, value, multi, getOptionId]);

  const submitEdit = useCallback(async () => {
    const trimmed = draftName.trim();
    if (!trimmed || !onRename || !editingId) return;
    setBusy(true);
    try {
      await onRename(editingId, trimmed);
      setIsInput(false);
      setEditingId(null);
      setDraftName('');
    } catch (_) {
      // keep input open
    } finally {
      setBusy(false);
    }
  }, [draftName, onRename, editingId]);

  const handleDelete = useCallback(
    (optionId) => async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!onDelete) return;
      try {
        await onDelete(optionId);
        if (!multi && value === optionId) onChange(null);
        if (multi && Array.isArray(value) && value.includes(optionId)) {
          onChange(value.filter((v) => v !== optionId));
        }
      } catch (_) {
        // host surfaces blocked-modal via thunk rejection
      }
    },
    [onDelete, value, multi, onChange],
  );

  const renderRow = useCallback(
    (option) => {
      const optionId = getOptionId(option);
      return (
        <div className={classes.row}>
          {multi && (
            <Checkbox
              checked={selectedIds.includes(optionId)}
              color="primary"
              size="small"
              className={classes.checkbox}
            />
          )}
          <span className={classes.rowLabel}>{getOptionLabel(option)}</span>
          <div className={classes.rowActions}>
            {onRename && (
              <Button
                color="primary"
                className={classes.iconButton}
                onClick={enterEdit(option)}
                disableRipple
                aria-label="edit"
              >
                <img src={PencilI} className={classes.optionsIcons} alt="edit" />
              </Button>
            )}
            {onDelete && (
              <Button
                color="primary"
                className={classes.iconButton}
                onClick={handleDelete(optionId)}
                disableRipple
                aria-label="delete"
              >
                <img src={CloseIcon} alt="delete" className={classes.optionsIcons} />
              </Button>
            )}
          </div>
        </div>
      );
    },
    [classes, multi, selectedIds, getOptionId, getOptionLabel, onRename, onDelete, enterEdit, handleDelete],
  );

  if (isInput) {
    const isEdit = !!editingId;
    return (
      <TextField
        value={draftName}
        onChange={(e) => setDraftName(e.target.value)}
        type="text"
        className={classes.textField}
        variant="outlined"
        fullWidth
        disabled={disabled || busy}
        autoFocus
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <div className={classes.inlineFlex}>
                <Button
                  variant="text"
                  className={classnames(classes.inputButton, classes.primaryButton)}
                  onClick={isEdit ? submitEdit : submitAdd}
                  disabled={!draftName.trim() || busy}
                  disableRipple
                >
                  {isEdit ? 'Save' : 'Add'}
                </Button>
                <Button
                  variant="text"
                  className={classes.inputButton}
                  onClick={cancelInput}
                  disableRipple
                  disabled={busy}
                >
                  Cancel
                </Button>
              </div>
            </InputAdornment>
          ),
        }}
      />
    );
  }

  return (
    <SearchSelect
      value={placeholderOnly && !multi ? null : value}
      onChange={onChange}
      options={visibleOptions}
      getOptionId={getOptionId}
      getOptionLabel={getOptionLabel}
      renderOption={renderRow}
      footer={
        onCreate && (
          <Button
            variant="text"
            className={classes.addButton}
            onClick={enterAdd}
            disableRipple
          >
            {addLabel}
          </Button>
        )
      }
      multiple={multi}
      disabled={disabled}
      error={error}
      classes={{
        option: classes.option,
        inputRoot: placeholderOnly ? classes.hiddenTags : undefined,
      }}
      inputProps={{ id, name, placeholder, onBlur }}
    />
  );
};

InlineCreateSelector.propTypes = {
  value: propTypes.oneOfType([
    propTypes.string,
    propTypes.arrayOf(propTypes.string),
  ]),
  options: propTypes.arrayOf(propTypes.object).isRequired,
  onChange: propTypes.func.isRequired,
  onCreate: propTypes.func,
  onRename: propTypes.func,
  onDelete: propTypes.func,
  getOptionId: propTypes.func,
  getOptionLabel: propTypes.func,
  excludeIds: propTypes.arrayOf(propTypes.string),
  addLabel: propTypes.string,
  placeholder: propTypes.string,
  disabled: propTypes.bool,
  multi: propTypes.bool,
  error: propTypes.bool,
  name: propTypes.string,
  id: propTypes.string,
  onBlur: propTypes.func,
  placeholderOnly: propTypes.bool,
};

InlineCreateSelector.defaultProps = {
  value: null,
  onCreate: undefined,
  onRename: undefined,
  onDelete: undefined,
  getOptionId: (option) => option?._id ?? option?.id ?? option?.value,
  getOptionLabel: (option) => option?.name ?? option?.label ?? '',
  excludeIds: [],
  addLabel: '+ Add',
  placeholder: '',
  disabled: false,
  multi: false,
  error: false,
  name: undefined,
  id: undefined,
  onBlur: undefined,
  placeholderOnly: false,
};

export default InlineCreateSelector;
